import React, { CSSProperties, useCallback, useEffect, useRef } from 'react';
import Highcharts, { AxisLabelsFormatterContextObject } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useStacklineTheme, hexToRgba } from '@stackline/ui';
import ReactDOMServer from 'react-dom/server';
import { getDirection, getIconForMetricChange } from 'src/components/BeaconV2Components/utils/chartStyles';
import { useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import colors from 'src/utils/colors';

type XAxisLabelContext = Omit<AxisLabelsFormatterContextObject, 'value'> & {
  value: any; // This depends on the categories passed in
};

/**
 * Function to calculate the tooltip position for the spider chart.
 * It should render the tooltip near the point with the largest value.
 */
export const spiderChartTooltipPositioner = ({
  tooltipContext,
  labelWidth,
  labelHeight,
  points
}: {
  tooltipContext: Highcharts.Tooltip;
  labelWidth: number;
  labelHeight: number;
  points?: Highcharts.Point[];
}) => {
  const { chart } = tooltipContext;
  const tooltipSizeXOffset = labelWidth / 2;
  const tooltipSizeYOffset = labelHeight / 2;
  const centerX = (chart as any)?.pane?.[0]?.center[0] + chart.plotLeft;
  const centerY = (chart as any)?.pane?.[0]?.center[1] + chart.plotTop;
  if (!points?.length) {
    // display in the center
    return {
      x: centerX - tooltipSizeXOffset,
      y: centerY - tooltipSizeYOffset
    };
  }
  const point = points.sort((a, b) => b.y - a.y)?.[0];

  const xPos = (point as any).plotX + chart.plotLeft;
  const yPos = (point as any).plotY + chart.plotTop;
  const radianAngle = Math.atan2(yPos - centerY, xPos - centerX);
  const distance = 35; // distance from point
  const xOffset = Math.cos(radianAngle) * distance;
  const yOffset = Math.sin(radianAngle) * distance;

  const x = xPos - tooltipSizeXOffset + xOffset;
  const y = yPos - tooltipSizeYOffset + yOffset;

  return {
    x,
    y
  };
};

const SpiderChartToolTip = ({
  points,
  formatMetric
}: {
  points: Highcharts.TooltipFormatterContextObject[];
  formatMetric: (num: number) => string;
}) => {
  const [comparisonPoint, currentPoint] = points;
  return (
    <div
      style={{
        display: points?.length > 0 ? 'flex' : 'none',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'white',
        borderRadius: '32px',
        minHeight: `32px`,
        padding: '4px 8px 4px 8px',
        textAlign: 'center',
        overflow: 'hidden',
        boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.05)',
        fontSize: '14px',
        fontWeight: '500',
        zIndex: 999
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <div
            style={{ backgroundColor: `${currentPoint?.color}`, width: '6px', height: '6px', borderRadius: '999px' }}
          ></div>
          <div
            style={{
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.96px',
              textAlign: 'left'
            }}
          >
            {formatMetric(currentPoint?.y)}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <div
            style={{ backgroundColor: `${comparisonPoint?.color}`, width: '6px', height: '6px', borderRadius: '999px' }}
          ></div>
          <div
            style={{
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.96px',
              textAlign: 'left'
            }}
          >
            {formatMetric(comparisonPoint?.y)}
          </div>
        </div>
      </div>
    </div>
  );
};

// Labels found on the exterior of the spider chart
const XAxisLabel = ({
  context,
  labelStyles = {},
  formatMetric
}: {
  context: XAxisLabelContext;
  labelStyles?: CSSProperties;
  formatMetric: (num: number) => string;
}) => {
  const { value } = context;
  const { name: xAxisLabel, value: primaryValue, change } = value; // TODO: We should not assume "score" is always present

  const defaultTextStyle = {
    fontFamily: 'Roboto',
    color: colors.darkBlue,
    fontSize: '26px',
    fontWeight: 700,
    lineHeight: '40px',
    letterSpacing: '0.08px',
    textAlign: 'left'
  };

  const direction = getDirection(change);

  const transform =
    direction === 'increasing' ? 'rotate(0deg)' : direction === 'decreasing' ? 'rotate(180deg)' : 'rotate(0deg)';

  const changeLabel =
    change !== undefined ? (
      <div
        style={{
          display: 'flex',
          marginLeft: '8px',
          alignItems: 'center',
          gap: '4px',
          fontSize: '14px',
          fontWeight: 500
        }}
      >
        <div style={{ transform }}>{getIconForMetricChange(change, 3)}</div>
        {formatMetric(Math.abs(change))}
      </div>
    ) : undefined;

  return (
    <div
      className="clickableLabel"
      tabIndex={0}
      role="button"
      aria-label={`${xAxisLabel}: ${formatMetric(primaryValue)}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.currentTarget.click();
        }
      }}
      style={
        {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          width: '170px',
          flexDirection: 'column',
          cursor: 'pointer',
          ...defaultTextStyle,
          ...labelStyles
        } as CSSProperties
      }
    >
      <span
        style={{ display: 'flex', alignItems: 'center', margin: 0, fontSize: '16px', fontWeight: 500, height: '24px' }}
      >
        {xAxisLabel}
      </span>
      <div style={{ display: 'flex', padding: 0, height: '40px' }}>
        <span style={{ margin: 0, fontSize: '26px', fontWeight: 700 }}>{formatMetric(primaryValue)}</span>
        {changeLabel}
      </div>
    </div>
  );
};

export interface SpiderChartLabelStyles {
  [key: number]: CSSProperties;
}

export type LabelClickFunction = ({ chart, labelData }: { chart: Highcharts.Chart; labelData: unknown }) => void;

/** App Spider Chart that is used in Content V2 */
export interface SpiderChartProps {
  primaryData: number[];
  comparisonData?: number[];
  categories: any[];
  getChartOptions?: (defaultOptions: Highcharts.Options) => Highcharts.Options;
  // Label props
  renderXAxisLabel?: (context: XAxisLabelContext) => string;
  /**
   * Styles for the labels on the spider chart.
   * The key is the position of the label on the chart (base 0).
   */
  labelStyles?: SpiderChartLabelStyles;
  /**
   * Function to handle the click event on the spider chart labels
   */
  handleLabelClick?: LabelClickFunction;
  /**
   * Width of the chart in pixels
   */
  chartWidth?: number;
  /**
   * Height of the chart in pixels
   */
  chartHeight?: number;
}

const SpiderChart = ({
  getChartOptions,
  primaryData,
  comparisonData,
  renderXAxisLabel,
  categories,
  labelStyles,
  handleLabelClick
}: SpiderChartProps) => {
  const chartRef = useRef<{ chart: Highcharts.Chart } | null>(null);

  const theme = useStacklineTheme();
  const metricFormatter = useMetricFormatter();
  const formatPercent = (value: number) =>
    metricFormatter(value, METRICTYPE.PERCENT, { showFullValue: true, decimalPlaces: 1 });

  /**
   * Function to attach listeners to the labels on the spider chart.
   * Highcharts does not provide a way to attach listeners to the labels directly.
   */
  const attachLabelListeners = useCallback(
    (chart: Highcharts.Chart) => {
      try {
        // Only proceed if handleLabelClick is provided
        if (!handleLabelClick) {
          return;
        }

        // Access the chart container directly instead of using labelGroup
        const { container } = chart;
        if (!container) {
          return;
        }

        const spans = container.querySelectorAll('.clickableLabel');

        Array.from(spans).forEach((span, index) => {
          const category = chart?.xAxis?.[0]?.categories?.[index] as unknown;

          if (span instanceof HTMLElement) {
            // Remove any existing click event listeners first to prevent duplicates
            span.removeEventListener('click', () => {});

            // Add the click event listener
            span.addEventListener('click', () => {
              if (handleLabelClick) {
                handleLabelClick({ chart, labelData: category });
              }
            });

            span.style.cursor = 'pointer';
          }
        });
      } catch (e) {
        console.error('Error attaching label listeners in SpiderChart:', e);
      }
    },
    [handleLabelClick]
  );

  useEffect(() => {
    if (chartRef.current && handleLabelClick) {
      const { chart } = chartRef.current;
      attachLabelListeners(chart);
    }
  }, [attachLabelListeners, handleLabelClick]);

  const getOptions = useCallback(() => {
    const defaultOptions: Highcharts.Options = {
      credits: {
        enabled: false
      },
      chart: {
        polar: true,
        type: 'areaspline',
        height: 448,
        width: 448,
        style: {
          overflow: 'visible'
        },
        spacing: [0, 0, 0, 0],
        margin: [0, 0, 0, 0],
        animation: false,
        events: {
          // Add a load event to attach label listeners after chart is loaded
          load() {
            if (handleLabelClick) {
              attachLabelListeners(this);
            }
          },
          // Add a redraw event to reattach label listeners after chart redraws
          redraw() {
            if (handleLabelClick) {
              attachLabelListeners(this);
            }
          },
          // Add a render event to ensure consistent positioning
          render() {
            // Force consistent positioning of labels after render
            const xAxis = this.xAxis[0];
            if (xAxis && xAxis.labelGroup) {
              xAxis.labelGroup.attr({ translateX: 0, translateY: 0 });
            }
          }
        }
      },
      pane: {
        startAngle: 0,
        endAngle: 360,
        size: '100%',
        center: ['50%', '50%'],
        background: [
          {
            backgroundColor: '#FBFBFB',
            borderColor: theme.colors.secondaryPorcelain,
            borderWidth: 1.8,
            outerRadius: '100%'
          }
        ]
      },
      plotOptions: {
        areaspline: {
          clip: false,
          connectEnds: true,
          connectNulls: true,
          pointPlacement: 'on',
          animation: false,
          marker: {
            enabled: true,
            radius: 1,
            lineWidth: 1,
            symbol: 'circle',
            states: {
              hover: {
                enabled: true
              }
            }
          },
          states: {
            hover: {
              enabled: false
            },
            inactive: {
              enabled: false
            }
          }
        },
        series: {
          animation: false,
          dataLabels: {
            enabled: false
          }
        }
      },

      series: [
        {
          name: 'Prior Year',
          type: 'areaspline',
          data: comparisonData,
          color: theme.colors.casper,
          fillColor: hexToRgba(theme.colors.casper, 0.1),
          marker: {
            enabled: true,
            lineWidth: 2.5,
            fillColor: theme.colors.casper,
            lineColor: '#D2DBE3',
            symbol: 'circle',
            radius: 4
          }
        },
        {
          name: 'Current Period',
          type: 'areaspline',
          data: primaryData,
          color: theme.colors.success,
          fillColor: hexToRgba(theme.colors.success, 0.1),
          marker: {
            enabled: true,
            lineWidth: 2.5,
            lineColor: '#BADDC6',
            symbol: 'circle',
            radius: 4,
            fillColor: '#71C184'
          }
        }
      ] as Highcharts.Options['series'],
      tooltip: {
        shared: true,
        backgroundColor: 'transparent',
        shadow: false,
        borderWidth: 0,
        enabled: true,
        followPointer: false,
        padding: 0,
        useHTML: true,
        formatter() {
          return ReactDOMServer.renderToStaticMarkup(
            <SpiderChartToolTip formatMetric={formatPercent} points={this.points} />
          );
        },
        positioner(labelWidth, labelHeight) {
          return spiderChartTooltipPositioner({
            tooltipContext: this,
            labelWidth,
            labelHeight,
            points: this.chart.hoverPoints
          });
        }
      },
      title: {
        text: ''
      },
      xAxis: {
        categories,
        tickmarkPlacement: 'on',
        lineWidth: 0,
        gridLineColor: '#EFF2F5',
        gridLineWidth: 1.8,
        labels: {
          distance: 35,
          step: 1,
          useHTML: true,

          align: 'center',
          formatter() {
            if (renderXAxisLabel) {
              return renderXAxisLabel(this);
            }

            const labelPosition = this?.pos ?? 0;

            const styles = {
              cursor: handleLabelClick ? 'pointer' : 'default',
              position: 'absolute',
              ...(labelStyles?.[labelPosition] ?? {})
            };

            return ReactDOMServer.renderToStaticMarkup(
              <XAxisLabel formatMetric={formatPercent} context={this} labelStyles={styles} />
            );
          }
        }
      },

      yAxis: {
        gridLineInterpolation: 'polygon',
        gridLineWidth: 0,
        lineWidth: 0,
        min: -0.27,
        max: 1.1,
        startOnTick: false,
        endOnTick: false,
        labels: {
          enabled: false
        }
      },
      legend: {
        enabled: false
      }
    };

    return defaultOptions;
  }, [
    theme,
    primaryData,
    comparisonData,
    categories,
    renderXAxisLabel,
    labelStyles,
    handleLabelClick,
    attachLabelListeners
  ]);

  const options = getChartOptions ? getChartOptions(getOptions()) : getOptions();

  return <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />;
};

export default SpiderChart;
